import { ref } from "vue";

export interface Toast {
  id: string;
  message: string;
  type: "success" | "error" | "warning" | "info";
  duration?: number;
  autoClose?: boolean;
}

// Global toast state
const toasts = ref<Toast[]>([]);
const toastCounter = ref(0);

export function useToast() {
  
  const addToast = (
    message: string,
    type: Toast["type"] = "info",
    duration = 4000,
    autoClose = true
  ): string => {
    const id = `toast-${++toastCounter.value}`;
    
    const toast: Toast = {
      id,
      message,
      type,
      duration,
      autoClose
    };
    
    toasts.value.push(toast);
    
    if (autoClose) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }
    
    return id;
  };
  
  const removeToast = (id: string): void => {
    const index = toasts.value.findIndex(toast => toast.id === id);
    if (index > -1) {
      toasts.value.splice(index, 1);
    }
  };
  
  const clearAllToasts = (): void => {
    toasts.value = [];
  };
  
  // Convenience methods for different types
  const success = (message: string, duration?: number): string => {
    return addToast(message, "success", duration);
  };
  
  const error = (message: string, duration = 6000): string => {
    return addToast(message, "error", duration);
  };
  
  const warning = (message: string, duration?: number): string => {
    return addToast(message, "warning", duration);
  };
  
  const info = (message: string, duration?: number): string => {
    return addToast(message, "info", duration);
  };
  
  return {
    toasts: toasts,
    addToast,
    removeToast,
    clearAllToasts,
    success,
    error,
    warning,
    info
  };
}